import { NavLink, useLocation } from "react-router-dom";
import { useAuth, ROLE_LABELS, can } from "@/lib/auth";
import {
  LayoutDashboard, Users, UserSquare2, Briefcase, ShoppingCart, Wallet,
  ArrowDownToLine, ArrowUpFromLine, BookOpenCheck, Award, Gift, FileBarChart,
  Settings, History, LogOut, Bell, ChevronDown, Calculator, ExternalLink, Printer, FileSignature, TrendingUp, MessageSquare, BookUser, Menu, X, GraduationCap
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ProfileDialog from "@/components/ProfileDialog";

const allItems = [
  { to: "/portal", label: "Royalties", icon: LayoutDashboard, key: "view_portal", autor: true },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/clientes", label: "Clientes", icon: Users, key: "manage_clients", vendor: true },
  { to: "/vendedores", label: "Vendedores", icon: UserSquare2, key: "dashboard" },
  { to: "/servicos", label: "Serviços", icon: Briefcase, key: "dashboard" },
  { to: "/vendas", label: "Vendas", icon: ShoppingCart, key: "manage_sales", skipForAdmin: true, vendor: true },
  { to: "/recebimentos", label: "Recebimentos", icon: ArrowDownToLine, key: "view_financial", vendor: true },
  { to: "/contas-receber", label: "Contas a Receber", icon: BookOpenCheck, key: "view_financial", vendor: true },
  { to: "/contas-pagar", label: "Contas a Pagar", icon: ArrowUpFromLine, key: "view_financial" },
  { to: "/caixa", label: "Caixa", icon: Wallet, key: "view_financial" },
  { to: "/previsao", label: "Previsão E/S", icon: TrendingUp, key: "view_financial" },
  { to: "/comissoes", label: "Comissões", icon: Award, key: "view_commissions", vendor: true },
  { to: "/bonificacoes", label: "Bonificações", icon: Gift, key: "view_commissions", vendor: true },
  { to: "/relatorios", label: "Relatórios", icon: FileBarChart, key: "view_reports", skipForAdmin: true, vendor: true },
  { to: "/contratos", label: "Contratos", icon: FileSignature, key: "generate_contracts" },
  { to: "/autores", label: "Autores", icon: BookUser, key: "manage_authors" },
  { to: "/usuarios", label: "Usuários", icon: Settings, key: "manage_users" },
  { to: "/treinamento", label: "Treinamento", icon: GraduationCap, always: true },
  { to: "/auditoria", label: "Auditoria", icon: History, key: "view_audit" },
];

function NotificationBell() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/notifications").then(r => setItems(r.data || [])).catch(() => {});
  }, []);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="topbar-notifications" className="relative p-2 hover:bg-secondary rounded-sm transition-colors">
          <Bell className="w-4 h-4" />
          {items.length > 0 && (
            <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notificações</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground text-center">Nenhuma notificação</div>
        ) : items.slice(0, 8).map((n, i) => (
          <DropdownMenuItem key={i} className="flex flex-col items-start gap-0.5 py-2">
            <div className="font-semibold text-xs">{n.title}</div>
            <div className="text-xs text-muted-foreground">{n.msg}</div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children, title, subtitle }) {
  const { user, logout, refresh } = useAuth();
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Messaging heartbeat + unread badge
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const ping = () => api.post("/messaging/heartbeat").catch(() => {});
    const loadUnread = () => api.get("/messaging/unread")
      .then(r => { if (!cancelled) setUnreadMsgs(r.data?.unread || 0); })
      .catch(() => {});
    ping(); loadUnread();
    const tPing = setInterval(ping, 30000);
    const tUnread = setInterval(loadUnread, 10000);
    return () => { cancelled = true; clearInterval(tPing); clearInterval(tUnread); };
  }, [user]);

  if (!user || user === false) return null;

  const items = allItems.filter(item => {
    if (item.always) return true;  // Treinamento — visível a TODOS os perfis
    if (user.role === "autor") return item.autor === true || item.to === "/mensagens";
    if (item.autor) return false; // Portal item hidden from non-autor roles
    if (user.role === "prestador") return item.prestador === true || item.to === "/mensagens";
    if (user.role === "vendedor") return item.vendor === true || item.to === "/";
    if (item.key === "dashboard") return true;
    if (item.skipForAdmin && user.role === "administrativo") return false;
    return can(user, item.key);
  });

  const initials = (user.name || "U").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Backdrop for mobile drawer */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`sidebar w-64 flex-shrink-0 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out lg:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 flex items-center gap-3 border-b border-[hsl(var(--sidebar-border))]">
          <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center overflow-hidden ring-1 ring-[hsl(var(--sidebar-border))]">
            <img
              src="/brand/marupiara-logo.png"
              alt="Marupiara"
              className="w-full h-full object-cover"
              data-testid="sidebar-logo"
            />
          </div>
          <div className="flex-1">
            <div className="font-display text-base text-white tracking-tight leading-none">Marupiara</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--gold-soft))] mt-1">Editoração</div>
          </div>
          <button
            data-testid="sidebar-close-mobile"
            className="lg:hidden text-[hsl(var(--sidebar-text))] hover:text-[hsl(var(--gold-soft))] p-1"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5 scrollbar-thin">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.to.replace(/\//g, "") || "dashboard"}`}
              className={({ isActive }) =>
                `sidebar-item relative ${isActive ? "active" : ""}`
              }
            >
              <it.icon className="w-4 h-4 flex-shrink-0" />
              <span>{it.label}</span>
              {it.badge === "unread_msgs" && unreadMsgs > 0 && (
                <span data-testid="sidebar-unread-msgs" className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-bold">
                  {unreadMsgs > 99 ? "99+" : unreadMsgs}
                </span>
              )}
            </NavLink>
          ))}
          {user.role !== "autor" && (
            <NavLink
              to="/orcamento"
              data-testid="nav-orcamento"
              className={({ isActive }) => `sidebar-item relative ${isActive ? "active" : ""}`}
            >
              <Calculator className="w-4 h-4 flex-shrink-0" />
              <span>Orçamento</span>
            </NavLink>
          )}
          {(user.role === "gestao" || user.role === "administrativo") && (
            <a
              href="https://pem.metabrasil.com.br/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-pedido-grafica"
              className="sidebar-item relative group"
            >
              <Printer className="w-4 h-4 flex-shrink-0" />
              <span>Pedido Gráfica</span>
              <ExternalLink className="w-3 h-3 ml-auto opacity-50 group-hover:opacity-100" />
            </a>
          )}
        </nav>
        <div className="p-3 border-t border-[hsl(var(--sidebar-border))]">
          <button
            data-testid="sidebar-logout"
            onClick={logout}
            className="sidebar-item w-full"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card sticky top-0 z-30 flex items-center justify-between px-3 sm:px-6 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              data-testid="topbar-menu"
              className="lg:hidden p-2 -ml-1 hover:bg-secondary rounded-sm transition-colors relative"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu className="w-5 h-5" />
              {unreadMsgs > 0 && (
                <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[9px] font-bold flex items-center justify-center">
                  {unreadMsgs > 9 ? "9+" : unreadMsgs}
                </span>
              )}
            </button>
            <div className="min-w-0">
              <h1 className="font-display text-base sm:text-xl tracking-tight truncate" data-testid="page-title">{title}</h1>
              {subtitle && <p className="text-[11px] sm:text-xs text-muted-foreground truncate hidden sm:block">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-user" className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary rounded-sm transition-colors">
                  <Avatar className="w-7 h-7">
                    {user.photo_url ? <AvatarImage src={user.photo_url} /> : null}
                    <AvatarFallback className="text-[11px] bg-primary text-primary-foreground">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <div className="text-xs font-semibold leading-tight">{user.name}</div>
                    <div className="text-[10px] text-muted-foreground">{ROLE_LABELS[user.role]}</div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-muted-foreground hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user.name}</span>
                    <span className="text-[10px] text-muted-foreground font-normal">@{user.username || user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem><Badge variant="secondary">{ROLE_LABELS[user.role]}</Badge></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowProfile(true)} data-testid="user-menu-profile">
                  <Settings className="w-4 h-4 mr-2" /> Meu Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="user-menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="p-3 sm:p-6 max-w-[1600px] mx-auto w-full">{children}</div>
        </main>
      </div>
      {showProfile && <ProfileDialog open={showProfile} onClose={() => setShowProfile(false)} onSaved={() => refresh && refresh()} />}
    </div>
  );
}
